import { useMemo, useState, type FormEvent } from "react";
import { Button } from "../../components/ui/Button";
import { usePresetsQuery, useLogActivity, type ActivityType } from "./api";
import { toKolkataDateInput, todayKolkataInput } from "../../lib/format";

const TYPES: { value: ActivityType; label: string }[] = [
  { value: "call", label: "Call" },
  { value: "meeting", label: "Meeting" },
  { value: "note", label: "Note" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
];

interface Props {
  workspaceSlug: string | null;
  leadId: string;
  // The lead's current next follow-up (ISO) — prefills the picker so the rep
  // sees and can advance the existing follow-up while logging.
  currentFollowUpAt?: string | null;
}

export function ActivityComposer({ workspaceSlug, leadId, currentFollowUpAt }: Props) {
  const { data } = usePresetsQuery(workspaceSlug);
  const log = useLogActivity(workspaceSlug);
  const [type, setType] = useState<ActivityType>("call");
  const [presetId, setPresetId] = useState<string>("");
  const [body, setBody] = useState("");
  const [applyStatus, setApplyStatus] = useState(true);
  const [followUp, setFollowUp] = useState<string>(
    toKolkataDateInput(currentFollowUpAt)
  );

  const presetsForType = useMemo(
    () => (data?.presets ?? []).filter((p) => p.type === type),
    [data, type]
  );
  const selectedPreset = presetsForType.find((p) => p.id === presetId);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await log.mutateAsync({
      leadId,
      type,
      ...(presetId ? { presetId } : {}),
      ...(body.trim() ? { body: body.trim() } : {}),
      ...(followUp ? { followUpAt: followUp } : {}),
      applySuggestedStatus: applyStatus,
    });
    setBody("");
  };

  return (
    <form onSubmit={onSubmit} className="composer">
      <div className="composer-row">
        <select value={type} onChange={(e) => { setType(e.target.value as ActivityType); setPresetId(""); }}>
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={presetId} onChange={(e) => setPresetId(e.target.value)}>
          <option value="">— preset (optional) —</option>
          {presetsForType.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        placeholder={selectedPreset?.label ? `Notes for "${selectedPreset.label}" (optional)` : "Notes (optional)"}
        className="composer-body"
      />
      <div className="composer-row">
        <label className="composer-followup">
          <span className="composer-followup-label">Follow up on</span>
          <input
            type="date"
            value={followUp}
            min={todayKolkataInput()}
            onChange={(e) => setFollowUp(e.target.value)}
            aria-label="Follow-up date"
          />
          {followUp ? (
            <button
              type="button"
              className="composer-followup-clear"
              onClick={() => setFollowUp("")}
              aria-label="Clear follow-up"
            >
              Clear
            </button>
          ) : null}
        </label>
      </div>
      <div className="composer-actions">
        {selectedPreset?.suggestedNextStatus ? (
          <label className="composer-checkbox">
            <input
              type="checkbox"
              checked={applyStatus}
              onChange={(e) => setApplyStatus(e.target.checked)}
            />
            Mark lead as <strong>{selectedPreset.suggestedNextStatus.replace("_", " ")}</strong>
          </label>
        ) : <span />}
        <Button type="submit" disabled={log.isPending}>
          {log.isPending ? "Saving…" : "Log activity"}
        </Button>
      </div>
      {log.isError ? <div className="form-error">{(log.error as Error).message}</div> : null}
    </form>
  );
}
